import React, { useCallback, useEffect, useState } from "react";
import { SummaryExpr, BooleanExpr } from "@aidkitorg/types/lib/survey";
import { generateDistroSummaryKind } from "../../../utils/adminPanel.utils";
import { DashboardMetric } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { HeroNumberWithFields } from "../../../Contexts/DashboardsContext";
import { ReportFilterConfig, BooleanExprToFilterGroupConverter, FilterGroupToBooleanExprConverter } from "@aidkitorg/types/lib/admin-panel/filters";

export const useMetricFormState = (component?: DashboardMetric) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [metricType, setMetricType] = useState('numeric');
  const [aggregation, setAggregation] = useState('Sum');
  const [dataField, setDataField] = useState('');

  const [numeratorAggregation, setNumeratorAggregation] = useState('Count');
  const [numerator, setNumerator] = useState('');
  const [denominatorAggregation, setDenominatorAggregation] = useState('Count');
  const [denominator, setDenominator] = useState('');

  const [decimalPlaces, setDecimalPlaces] = useState(2);

  const [filterConfig, setFilterConfig] = useState<ReportFilterConfig | undefined>(undefined);
  const [titleHasError, setTitleHasError] = useState(false);

  useEffect(() => {
    if (!component) return;

    const componentTitle = 'title' in component && component.title
      ? typeof component.title === 'string'
        ? component.title
        : component.title['en']
      : 'Dashboard Component';

    setTitle(componentTitle);
    setDescription(typeof component.description === 'object' ? component.description?.content?.en || '' : component.description || '');
    setAggregation(component.summary.kind);
    setDecimalPlaces(component.decimalPlaces ?? 2);

    if (component.suffix === '%' || component.summary.kind === 'Percentage') {
      setMetricType('percentage');
    } else if (component.prefix === '$') {
      setMetricType('currency');
    } else {
      setMetricType('numeric');
    }

    if (component.summary.kind === 'Percentage') {
      const { numerator, denominator } = component.summary;

      setNumeratorAggregation(numerator.kind);
      if (numerator.kind === 'Mean' || numerator.kind === 'Sum') {
        const numeratorValue = numerator.value

        // We need to assert that this is a field type
        if (numeratorValue.kind === 'Field') {
          setNumerator(numeratorValue.field)
        }
      }

      setDenominatorAggregation(denominator.kind);
      if (denominator.kind === 'Mean' || denominator.kind === 'Sum') {
        const denominatorValue = denominator.value

        // We need to assert that this is a field type
        if (denominatorValue.kind === 'Field') {
          setDenominator(denominatorValue.field)
        }
      }
    } else if (component.summary.kind === 'Sum' || component.summary.kind === 'Mean') {
      if (component.summary.value.kind === 'Field') {
        setDataField(component.summary.value.field);
      }
    }

    // Initialize filter config from component
    if (component.filter) {
      try {
        const convertedFilter = BooleanExprToFilterGroupConverter.convert(component.filter);
        if (convertedFilter) {
          setFilterConfig({ enabled: true, config: convertedFilter });
        }
      } catch (error) {
        console.warn('Failed to convert saved filter config:', error);
      }
    }
  }, [component]);

  const generatePercentageSummaryKind = useCallback((): SummaryExpr => ({
    kind: 'Percentage',
    numerator: generateDistroSummaryKind(numeratorAggregation, numerator),
    denominator: generateDistroSummaryKind(denominatorAggregation, denominator),
  }), [numeratorAggregation, numerator, denominatorAggregation, denominator]);

  const generateSummary = (): SummaryExpr =>
    metricType === 'percentage'
      ? generatePercentageSummaryKind()
      : generateDistroSummaryKind(aggregation, dataField);

  const buildMetricObject = (): HeroNumberWithFields => {
    const metric: HeroNumberWithFields = {
      kind: 'Hero Number',
      title,
      summary: generateSummary(),
      useCommas: true,
      ...(description && { description: { content: { en: description }, placement: 'top' as const } }),
    };

    if (metricType === 'percentage') {
      metric.suffix = '%';
    } else if (metricType === 'currency') {
      metric.prefix = '$';
      metric.decimalPlaces = 2;
    }

    // Set decimal places for Average (Mean) aggregation
    if (aggregation === 'Mean') {
      metric.decimalPlaces = decimalPlaces;
    }

    // Add filter if it's configured
    if (filterConfig && filterConfig.enabled && filterConfig.config) {
      const booleanExpr = FilterGroupToBooleanExprConverter.convertFromConfig(filterConfig);
      if (booleanExpr) {
        metric.filter = booleanExpr;
      }
    }

    return metric;
  };

  const percentageValid = (numeratorAggregation === 'Count' || numerator !== '') && (denominatorAggregation === 'Count' || denominator !== '')
  const nonPercentageValid = dataField !== '';
  const isFormStateValid = title !== '' && !titleHasError &&
    (
      (metricType === 'percentage' && percentageValid) ||
      (metricType !== 'percentage' && nonPercentageValid)
    )
  
  return {
    title, setTitle,
    description, setDescription,
    metricType, setMetricType,
    aggregation, setAggregation,
    dataField, setDataField,
    numeratorAggregation, setNumeratorAggregation,
    numerator, setNumerator,
    denominatorAggregation, setDenominatorAggregation,
    denominator, setDenominator,
    decimalPlaces, setDecimalPlaces,
    filterConfig, setFilterConfig,
    setTitleHasError,
    buildMetricObject,
    isFormStateValid
  };
};
